import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  LayoutDashboard, Building2, MessageSquare, LogOut, 
  Plus, Loader2, Menu, X, Users, UserCog, Layers, ChevronLeft, Settings
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import apiClient, { authApi, propertyApi } from "@/utils/api";
import DashboardStats from "@/components/admin/DashboardStats";
import PropertiesTable from "@/components/admin/PropertiesTable";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_propmatch-7/artifacts/y6a3qiy4_LogotipoFinal_IkonicDeals%28web%29.jpg";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);

  const handleLogout = useCallback(async () => {
    await authApi.logout();
    navigate("/panel-gestion-8k2m/acceso");
  }, [navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [propertiesRes, contactsRes] = await Promise.all([
        propertyApi.getAll(),
        apiClient.get('/contacts')
      ]);
      setProperties(propertiesRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  useEffect(() => {
    const userData = localStorage.getItem("admin_user");
    if (userData) {
      setUser(JSON.parse(userData));
    }
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm("¿Está seguro de eliminar esta propiedad?")) return;
    
    try {
      await propertyApi.delete(id);
      toast.success("Propiedad eliminada");
      setProperties(properties.filter(p => p.id !== id));
    } catch (error) {
      toast.error("Error al eliminar la propiedad");
    }
  };

  const formatPrice = (price) => `$${price?.toLocaleString() || 0}`;

  const navigation = [
    { name: "Propiedades", href: "/admin/dashboard", icon: LayoutDashboard, current: true },
    { name: "Mensajes", href: "/admin/mensajes", icon: MessageSquare },
    { divider: true, label: "CRM" },
    { name: "Dashboard CRM", href: "/admin/crm", icon: Users },
    { name: "Contactos", href: "/admin/crm/contacts", icon: Users },
    { name: "Equipo", href: "/admin/crm/team", icon: UserCog },
    { name: "Etapas", href: "/admin/crm/stages", icon: Layers },
    { divider: true, label: "Sistema" },
    { name: "Configuración", href: "/admin/settings", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a] border-b border-white/5 p-4 flex items-center justify-between">
        <span className="text-[#D4AF37] font-semibold">Ikonic Deals</span>
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white">
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </Button>
      </div>

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-40 h-screen w-64 bg-[#0a0a0a] border-r border-white/5 transform transition-transform lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-white/5">
            <Link to="/admin/dashboard" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Ikonic Deals" className="h-8 w-auto" />
              <span className="text-[#D4AF37] font-semibold">Admin</span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navigation.map((item, idx) => {
              if (item.divider) {
                return (
                  <div key={idx} className="pt-4 pb-2">
                    <p className="text-[#A3A3A3] text-xs uppercase tracking-wider px-3">{item.label}</p>
                  </div>
                );
              }
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    item.current ? "bg-[#D4AF37]/10 text-[#D4AF37]" : "text-[#A3A3A3] hover:text-white hover:bg-white/5"
                  }`}
                >
                  <item.icon size={18} />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* User */}
          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 mb-4 px-3">
              <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                <span className="text-[#D4AF37] font-semibold text-sm">
                  {user?.name?.charAt(0)?.toUpperCase() || "A"}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{user?.name || "Admin"}</p>
                <p className="text-[#A3A3A3] text-xs truncate">{user?.email}</p>
              </div>
            </div>
            <div className="space-y-1">
              <Link to="/" className="flex items-center gap-3 px-3 py-2 rounded-lg text-[#A3A3A3] hover:text-white hover:bg-white/5 transition-colors">
                <ChevronLeft size={18} />
                <span>Ver Sitio</span>
              </Link>
              <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[#A3A3A3] hover:text-red-500 hover:bg-red-500/5 transition-colors">
                <LogOut size={18} />
                <span>Cerrar Sesión</span>
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-4 sm:p-6 lg:p-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">Propiedades</h1>
              <p className="text-[#A3A3A3] mt-1">Gestiona tu catálogo de propiedades</p>
            </div>
            <Link to="/admin/propiedades/nueva">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#FFE082] gap-2">
                <Plus size={18} /> Nueva Propiedad
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-[#D4AF37]" size={40} />
            </div>
          ) : (
            <>
              {/* Stats */}
              <div className="mb-8">
                <DashboardStats properties={properties} contacts={contacts} />
              </div>

              {/* Properties Table */}
              <div className="bg-[#121212] border border-white/5 rounded-lg overflow-hidden">
                <div className="p-4 border-b border-white/5">
                  <h2 className="text-lg font-medium text-white">Listado de Propiedades</h2>
                </div>
                <PropertiesTable 
                  properties={properties} 
                  onDelete={handleDelete}
                  formatPrice={formatPrice}
                />
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
